import {
  FINAL_RADIUS_METERS,
  INITIAL_RADIUS_METERS,
  PICKUP_RADIUS_RATIO,
  PICKUP_VOLUME_EFFICIENCY,
  tierIndexForRadius,
} from "./config";

export interface GrowthSnapshot {
  readonly radiusMeters: number;
  readonly tierIndex: number;
  readonly pickupCount: number;
  readonly score: number;
  readonly finalScaleReached: boolean;
}

export interface AbsorbResult extends GrowthSnapshot {
  readonly accepted: boolean;
  readonly radiusBeforeMeters: number;
  readonly radiusGainMeters: number;
  readonly tierChanged: boolean;
  readonly points: number;
}

export class GrowthModel {
  private radiusCubed: number;
  private pickups = 0;
  private totalScore = 0;

  constructor(radiusMeters = INITIAL_RADIUS_METERS) {
    this.radiusCubed = Math.max(0.001, radiusMeters ** 3);
  }

  get radiusMeters(): number {
    return Math.cbrt(this.radiusCubed);
  }

  get tierIndex(): number {
    return tierIndexForRadius(this.radiusMeters);
  }

  get pickupCount(): number {
    return this.pickups;
  }

  get score(): number {
    return this.totalScore;
  }

  canAbsorb(itemRadiusMeters: number): boolean {
    const maximumRadiusMeters = this.radiusMeters * PICKUP_RADIUS_RATIO;
    const roundingTolerance = Number.EPSILON
      * Math.max(1, itemRadiusMeters, maximumRadiusMeters)
      * 4;
    return itemRadiusMeters <= maximumRadiusMeters + roundingTolerance;
  }

  absorb(itemRadiusMeters: number): AbsorbResult {
    const radiusBeforeMeters = this.radiusMeters;
    const tierBefore = this.tierIndex;
    if (!this.canAbsorb(itemRadiusMeters)) {
      return {
        ...this.snapshot(),
        accepted: false,
        radiusBeforeMeters,
        radiusGainMeters: 0,
        tierChanged: false,
        points: 0,
      };
    }

    const relativeSize = itemRadiusMeters / radiusBeforeMeters;
    const comboEfficiency = PICKUP_VOLUME_EFFICIENCY + Math.min(1.25, this.pickups * 0.004);
    this.radiusCubed += itemRadiusMeters ** 3 * comboEfficiency;
    this.pickups += 1;
    const points = Math.max(10, Math.round(relativeSize ** 2 * 850 * (tierBefore + 1)));
    this.totalScore += points;

    return {
      ...this.snapshot(),
      accepted: true,
      radiusBeforeMeters,
      radiusGainMeters: this.radiusMeters - radiusBeforeMeters,
      tierChanged: this.tierIndex !== tierBefore,
      points,
    };
  }

  setRadiusForDebug(radiusMeters: number): void {
    this.radiusCubed = Math.max(0.001, radiusMeters ** 3);
  }

  reset(): void {
    this.radiusCubed = INITIAL_RADIUS_METERS ** 3;
    this.pickups = 0;
    this.totalScore = 0;
  }

  snapshot(): GrowthSnapshot {
    const radiusMeters = this.radiusMeters;
    return {
      radiusMeters,
      tierIndex: this.tierIndex,
      pickupCount: this.pickups,
      score: this.totalScore,
      finalScaleReached: radiusMeters >= FINAL_RADIUS_METERS,
    };
  }
}
